import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';

interface RetentionPolicy {
  table: string;
  timeColumn: string;
  days: number;
  /** Rows matching this SQL predicate are never purged by age. */
  keepPredicate?: string;
}

/** Rows deleted per statement when a table is not partitioned. */
const DELETE_CHUNK = 5_000;
const MAX_CHUNKS_PER_RUN = 200;

function envDays(name: string, fallback: number): number {
  const raw = Number(process.env[name]);
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : fallback;
}

/**
 * Keeps the audit schema from growing without bound — which, before this, it
 * did: neither audit table had any purge at all, on a single VPS where the
 * audit schema shares a disk with the live database.
 *
 * Works whether or not the tables have been converted to monthly partitions
 * (migration 046). Partitioned: drop the partition, which is instant and leaves
 * no bloat. Not partitioned: delete in bounded chunks, so a backlog can never
 * turn into one enormous statement holding locks and generating a vacuum storm.
 */
@Injectable()
export class AuditRetentionService {
  private readonly logger = new Logger(AuditRetentionService.name);

  constructor(private readonly prisma: PrismaService) {}

  private policies(): RetentionPolicy[] {
    return [
      {
        table: 'interface_log',
        timeColumn: 'requestedOn',
        days: envDays('AUDIT_INTERFACE_RETENTION_DAYS', 30),
        // Server errors are the rows anyone actually goes looking for later.
        keepPredicate: `"statusCode" >= 500 AND "requestedOn" > now() - interval '90 days'`,
      },
      {
        table: 'activity_log',
        timeColumn: 'receivedAt',
        days: envDays('AUDIT_ACTIVITY_RETENTION_DAYS', 90),
      },
      {
        table: 'change_log',
        timeColumn: 'changedOn',
        days: envDays('AUDIT_CHANGE_RETENTION_DAYS', 400),
        // Money and identity are never purged by age. Deleting them is a
        // compliance decision, not a disk-space one.
        keepPredicate: `"schemaName" IN ('sales','platform') OR "tableName" IN ('users','stores','organizations','store_staff','payments','refunds','orders','order_items')`,
      },
      {
        table: 'change_batch',
        timeColumn: 'startedAt',
        days: envDays('AUDIT_CHANGE_RETENTION_DAYS', 400),
      },
    ];
  }

  @Cron('20 3 * * *')
  async purge(): Promise<void> {
    for (const policy of this.policies()) {
      try {
        const removed = (await this.isPartitioned(policy.table))
          ? await this.dropOldPartitions(policy)
          : await this.deleteInChunks(policy);

        if (removed > 0) {
          this.logger.log(`retention: ${policy.table} released ${removed} row(s)/partition(s)`);
        }
      } catch (error) {
        // One table's retention failing must not stop the others.
        this.logger.warn(
          `retention failed for ${policy.table}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
  }

  /** Creates next month's partitions ahead of time, so an insert never lands with no partition to go in. */
  @Cron('0 2 25 * *')
  async ensureFuturePartitions(): Promise<void> {
    for (const policy of this.policies()) {
      if (!(await this.isPartitioned(policy.table))) continue;

      try {
        await this.prisma.$executeRawUnsafe(
          `SELECT audit.ensure_partition('${policy.table}', date_trunc('month', now() + interval '1 month')::date)`
        );
        await this.prisma.$executeRawUnsafe(
          `SELECT audit.ensure_partition('${policy.table}', date_trunc('month', now() + interval '2 month')::date)`
        );
      } catch (error) {
        this.logger.warn(
          `could not pre-create partitions for ${policy.table}: ${
            error instanceof Error ? error.message : String(error)
          }`
        );
      }
    }
  }

  async isPartitioned(table: string): Promise<boolean> {
    const rows = await this.prisma.$queryRawUnsafe<{ partitioned: boolean }[]>(
      `SELECT c.relkind = 'p' AS partitioned
         FROM pg_class c JOIN pg_namespace n ON n.oid = c.relnamespace
        WHERE n.nspname = 'audit' AND c.relname = $1`,
      table
    );

    return rows[0]?.partitioned === true;
  }

  private async dropOldPartitions(policy: RetentionPolicy): Promise<number> {
    const cutoff = `now() - interval '${policy.days} days'`;

    // Detach-then-drop rather than a plain DROP: detaching takes a lighter lock
    // and leaves the data recoverable for the moment between the two if a purge
    // ever turns out to have been premature.
    const dropped = await this.prisma.$executeRawUnsafe(`
      DO $$
      DECLARE p RECORD;
      BEGIN
        FOR p IN
          SELECT c.relname
            FROM pg_class c
            JOIN pg_inherits i ON i.inhrelid = c.oid
            JOIN pg_class parent ON parent.oid = i.inhparent
            JOIN pg_namespace n ON n.oid = c.relnamespace
           WHERE n.nspname = 'audit'
             AND parent.relname = '${policy.table}'
             AND substring(c.relname from '\\d{4}_\\d{2}$') IS NOT NULL
             AND to_date(substring(c.relname from '\\d{4}_\\d{2}$'), 'YYYY_MM')
                 < date_trunc('month', ${cutoff})
        LOOP
          EXECUTE format('ALTER TABLE audit.%I DETACH PARTITION audit.%I', '${policy.table}', p.relname);
          EXECUTE format('DROP TABLE audit.%I', p.relname);
        END LOOP;
      END $$;
    `);

    return dropped;
  }

  private async deleteInChunks(policy: RetentionPolicy): Promise<number> {
    const keep = policy.keepPredicate ? `AND NOT (${policy.keepPredicate})` : '';
    let total = 0;

    for (let chunk = 0; chunk < MAX_CHUNKS_PER_RUN; chunk += 1) {
      const removed = await this.prisma.$executeRawUnsafe(`
        DELETE FROM audit.${policy.table}
         WHERE ctid IN (
           SELECT ctid FROM audit.${policy.table}
            WHERE "${policy.timeColumn}" < now() - interval '${policy.days} days'
            ${keep}
            LIMIT ${DELETE_CHUNK}
         )
      `);

      total += removed;
      if (removed < DELETE_CHUNK) break;
    }

    return total;
  }

  /** Table sizes, for the readiness/metrics surface — a number someone sees monthly rather than a surprise. */
  async sizes(): Promise<{ table: string; bytes: number; rows: number }[]> {
    return this.prisma.$queryRawUnsafe(`
      SELECT c.relname AS table,
             pg_total_relation_size(c.oid)::bigint AS bytes,
             c.reltuples::bigint AS rows
        FROM pg_class c JOIN pg_namespace n ON n.oid = c.relnamespace
       WHERE n.nspname = 'audit' AND c.relkind IN ('r','p')
       ORDER BY pg_total_relation_size(c.oid) DESC
    `);
  }
}
